<template>
  <div>
    <v-dialog v-model="show" width="90%" max-width="1100px">
      <v-card>
        <v-card-title>
          {{ !canEdit ? 'Project details (read-only)' : 'Project details'}}
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="onCancelClick">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="project">
          <v-form
            ref="projectForm"
            v-model="form"
          >
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  style="font-weight: 600"
                  class="my-7"
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                  label="Project Name*"
                  v-model="project.Description"
                  :readonly="!canEdit"
                  :disabled="notFromProjectsModule"
                ></v-text-field>
                <v-text-field
                  class="my-7"
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                  label="Project Group*"
                  v-model="project.Project_Group"
                  :readonly="!canEdit"
                  :disabled="notFromProjectsModule"
                ></v-text-field>
                <v-autocomplete
                  class="my-7"
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                  label="Client*"
                  :items="clients"
                  item-text="Name"
                  item-value="Client_ID"
                  v-model="project.Client_ID"
                  @blur="onAutocompleteBlur('Client_ID', $event)"
                  :readonly="!canEdit"
                  :disabled="notFromProjectsModule"
                  :append-icon="!canEdit ? '' : undefined"
                ></v-autocomplete>
                <v-text-field
                  dense
                  hide-details
                  :label="project && project.Project_ID ? 'Department' : 'Department*'"
                  :rules="project && project.Project_ID ? [] : [rules.required]"
                  :value="
                    infoForCreatingProjectDepHistory.length && !project.Project_ID
                    ? infoForCreatingProjectDepHistory[0].Dep_Name
                    : project.Department"
                  :suffix="infoForCreatingProjectDepHistory.length && !project.Project_ID ? infoForCreatingProjectDepHistory[0].From_Date : null"
                  readonly
                  :disabled="notFromProjectsModule"
                >
                  <template v-slot:append v-if="project">
                    <v-btn
                      v-if="isHRorFinancierOrManager"
                      text
                      plain
                      small
                      color="primary"
                      @click.prevent="showDepartmentHistory = true">{{ project.Department ? (project.Project_ID ? 'Details...': 'Update...') : 'Add...' }}
                    </v-btn>
                  </template>
                </v-text-field>
                <v-textarea
                  class="my-7"
                  dense
                  rows="3"
                  counter="8000"
                  hide-details="auto"
                  :rules="[rules.maxLength(8000)]"
                  label="Notes"
                  v-model="project.Keywords"
                  :readonly="!canEdit"
                  :disabled="notFromProjectsModule"
                ></v-textarea>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  class="my-7"
                  label="Status*"
                  hide-details="auto"
                  :rules="[rules.required]"
                  dense
                  v-model="project.Status"
                  :items="projectStatuses"
                  item-text="Description"
                  item-value="Status_ID"
                  :readonly="!canEdit"
                  :disabled="notFromProjectsModule"
                  :append-icon="!canEdit ? '' : undefined"
                ></v-select>
                <div class="mt-5 mb-4">
                  <DatePicker
                    dense
                    hide-details
                    v-model="project.Get_Date"
                    label="Getting Date"
                    :readonly="!canEdit"
                    :disabled="notFromProjectsModule"
                  />
                </div>
                <div class="my-7">
                  <DatePicker
                    dense
                    hide-details
                    v-model="project.Start_Date"
                    label="Starting Date"
                    :readonly="!canEdit"
                    :disabled="notFromProjectsModule"
                    clearable
                  />
                </div>
                <div class="my-5">
                  <DatePicker
                    dense
                    hide-details
                    v-model="project.Release_Date"
                    label="Released Date"
                    :readonly="!canEdit"
                    :disabled="notFromProjectsModule"
                    clearable
                  />
                </div>
                <v-checkbox
                  style="display: inline-flex"
                  hide-details
                  dense
                  :disabled="notFromProjectsModule"
                  v-model="project.NDA"
                  label="NDA"
                >
                </v-checkbox>
              </v-col>
              <v-col cols="12" md="4" style="position: relative">
                <div class="no-project-id-placeholder" v-if="!(project && project.Project_ID)">
                  Save "project details" to enable editing of "Managers" and "Rate" section.
                </div>
                <div class="form-fields-container">
                  <v-text-field
                    class="my-7"
                    hide-details="auto"
                    dense
                    label="Project Manager"
                    :value="project.SBUFullName"
                    readonly
                    :disabled="notFromProjectsModule"
                  >
                    <template v-slot:append v-if="project && project.Project_ID">
                      <v-btn text plain small @click.prevent="openManagerHistoryDialog()" color="primary">Details...</v-btn>
                    </template>
                  </v-text-field>
                  <v-text-field
                    class="my-7"
                    hide-details="auto"
                    dense
                    label="Project Coordinator"
                    :value="project.PMFullName"
                    readonly
                    :disabled="notFromProjectsModule"
                  >
                    <template v-slot:append v-if="project && project.Project_ID">
                      <v-btn text plain small color="primary" @click.prevent="openManagerHistoryDialog()">Details...</v-btn>
                    </template>
                  </v-text-field>
                  <v-text-field
                    class="my-7"
                    hide-details="auto"
                    dense
                    label="Project Administrator"
                    :value="project.PAFullName"
                    readonly
                    :disabled="notFromProjectsModule"
                  >
                    <template v-slot:append v-if="project && project.Project_ID">
                      <v-btn text plain small color="primary" @click.prevent="openManagerHistoryDialog()">Details...</v-btn>
                    </template>
                  </v-text-field>
                  <v-text-field
                    class="my-7"
                    hide-details="auto"
                    dense
                    label="Account Manager"
                    :value="project.AMFullName"
                    readonly
                    :disabled="notFromProjectsModule"
                  >
                    <template v-slot:append v-if="project && project.Project_ID">
                      <v-btn text plain small color="primary" @click.prevent="openManagerHistoryDialog()">Details...</v-btn>
                    </template>
                  </v-text-field>
                  <v-text-field
                    dense
                    hide-details
                    label="Current Rate"
                    :value="currentRateValue"
                    readonly
                    :disabled="notFromProjectsModule || !isFinancier"
                  >
                    <template v-slot:append v-if="project && project.Project_ID">
                      <v-btn v-if="isFinancier" text plain small color="primary" @click.prevent="showRateHistory = true">Details...</v-btn>
                    </template>
                  </v-text-field>
                  <v-text-field
                    class="my-7"
                    dense
                    hide-details="auto"
                    label="Internal Budget"
                    :rules="[rules.number]"
                    :readonly="!canEdit"
                    :disabled="notFromProjectsModule"
                    v-model="project.Internal_Budget"
                  ></v-text-field>
                  <v-text-field
                    class="my-7"
                    dense
                    hide-details="auto"
                    label="External Budget"
                    :rules="[rules.number]"
                    :readonly="!canEdit"
                    :disabled="notFromProjectsModule"
                    v-model="project.External_Budget"
                  ></v-text-field>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            small
            outlined
            v-if="project && project.Project_ID"
            color="primary"
            @click.prevent="showAllWorkersOnCurrentProject"
          >
            Employees
          </v-btn>
          <v-btn
            small
            outlined
            v-if="project && project.Project_ID && canEdit"
            color="primary"
            @click.prevent="goToReportsNewPage"
          >
            Reports
          </v-btn>
          <v-btn
            small
            outlined
            v-if="showIntegrationButton"
            color="primary"
            @click.prevent="showIntegrationsDialog = true"
          >
            Integrations
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            v-if="project && canEdit && project.Project_ID"
            :color="project.Is_Deleted ? 'success white--text' : 'red darken-2 white--text'"
            @click.prevent="onDeleteClick"
          >
            {{ project.Is_Deleted ? 'Restore': 'Delete' }}
          </v-btn>
          <v-btn
            v-if="project && canEdit && project.Client_ID && project.Description && project.Project_Group && (project.Project_ID || project.Department || infoForCreatingProjectDepHistory.length)"
            small
            color="primary"
            :disabled="!canSave"
            @click.prevent="onSaveClick"
          >
            {{project.Project_ID ? 'Update' : 'Save'}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showIntegrationsDialog"
      max-width="600"
    >
      <v-card
        max-height="90vh"
      >
        <v-card-title>
          <div>
            <v-tabs
              width="auto"
              @change="onIntegrationTabChange"
              v-model="integrationsActiveTab"
              center-active
            >
              <v-tab
                v-for="(item, itemKey) in integrations"
                :key="itemKey"
                :disabled="item.isDisabled"
              >
                {{ item.tabName }}
              </v-tab>
            </v-tabs>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="showIntegrationsDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-tabs-items v-model="integrationsActiveTab">
            <v-tab-item
              v-for="(item, itemKey) in integrations"
              :key="itemKey"
            >
              <div class="mt-4" v-if="item.fields">
                <v-text-field
                  :disabled="notFromProjectsModule"
                  v-for="(field, fieldKey) in item.fields"
                  :key="fieldKey"
                  :label="field.label"
                  v-model="field.val"
                ></v-text-field>
                <div
                  v-if="jiraWebhookIntegrationLink && item.id === 'jira'"
                >
                  <div
                    class="d-flex align-center"
                  >
                    <v-text-field
                      label="Link For Jira Webhook"
                      :value="valueForJiraWebhookLink"
                      disabled
                    >
                    </v-text-field>
                    <v-btn
                      small
                      rounded
                      icon
                      color="primary"
                      @click="copyJiraWebhookLinkToClipboard"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="d-flex align-center mb-4">
                    <h4 class="mr-2">Import reports only for employee:</h4>
                    <v-switch
                      v-model="isEmployeeOnly"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                      :disabled="!canEdit"
                      @change="saveIsEmployeeOnly"
                    ></v-switch>
                  </div>
                  <v-data-table
                    densecurrentRateValue
                    class="data-table"
                    :items-per-page="-1"
                    :headers="canEdit ? integratedEmployeeHeaders : [integratedEmployeeHeaders[0]]"
                    :items="integratedEmployeeToJiraProject"
                    hide-default-footer
                  >
                    <template v-slot:[`item.Jira_Name`]="{ item }">
                      <v-text-field
                        v-if="canEdit"
                        dense
                        hide-details
                        :value="item.Jira_Name"
                        @change="updateEmployeeJiraName(item, $event)"
                        placeholder="Enter Jira Name"
                      ></v-text-field>
                      <span v-else>{{ item.Jira_Name }}</span>
                    </template>
                    <template v-slot:[`item.Actions`]="{ item }">
                      <v-btn
                        small
                        icon
                        color="red darken-2"
                        @click="deleteEmployeeFromJiraIntegration(item)"
                      >
                        <v-icon>mdi-delete-circle</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:[`body.append`] v-if="canEdit">
                      <tr>
                      <td class="text-start px-2 text-no-wrap v-data-table__divider">
                        <v-select v-if="addNewEmployeeToIntegrationMode"
                          dense
                          hide-details
                          :items="employee"
                          item-text="full_name"
                          item-value="employee_id"
                          v-model="newEmployeeForIntegrationID"
                        >
                          <template v-slot:prepend-item>
                            <div class="pa-2">
                              <v-text-field
                                label="Search employee"
                                hide-details
                                dense
                                v-model="searchEmployee"
                              ></v-text-field>
                            </div>
                          </template>
                        </v-select>
                      </td>
                      <td class="text-start px-2 text-no-wrap v-data-table__divider">
                        <v-text-field v-if="addNewEmployeeToIntegrationMode"
                          dense
                          hide-details
                          v-model="newEmployeeJiraName"
                          placeholder="Jira Name"
                        ></v-text-field>
                      </td>
                      <td class="text-center px-2 text-no-wrap">
                        <v-btn
                          small
                          icon
                          color="success"
                          :disabled="!newEmployeeForIntegrationID && addNewEmployeeToIntegrationMode"
                          @click="addNewEmployeeToJiraIntegration"
                        >
                          <v-icon v-if="addNewEmployeeToIntegrationMode">mdi-check-circle</v-icon>
                          <v-icon v-else>mdi-plus-circle</v-icon>
                        </v-btn>
                        <v-btn
                          small
                          icon
                          color="primary"
                           v-if="addNewEmployeeToIntegrationMode"
                          @click="addNewEmployeeToIntegrationMode = false"
                        >
                          <v-icon>mdi-close-circle</v-icon>
                        </v-btn>
                      </td>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
              </div>
              <div class="mt-4" v-else>Work in progress...</div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions v-if="canEdit">
          <v-spacer></v-spacer>
          <v-btn
            small
            v-if="integrationID"
            color="red darken-2 white--text"
            @click.prevent="showAlert = true"
          >Delete</v-btn>
          <v-btn
            small
            color="primary"
            :disabled="!canSaveOrUpdateIntegration"
            @click.prevent="saveIntegration"
          >{{integrationID ? 'Update': 'Save'}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="showAlert"
      v-model="showAlert"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="`You want to delete integration current project with ${activeIntegrationLabel}`"
      @onDeleteClick="deleteIntegration"
      @onCancelClick="showAlert = false"
      @onCloseClick="showAlert = false"
    />
    <v-dialog
      v-model="showWorkers"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Workers on current project
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="showWorkers = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :items="workers"
            :headers="workersOnProjectHeaders"
            @click:row="openEmployeeCard($event)"
          >
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ManagersHistory
      v-if="project && project.Project_ID"
      v-model="showManagersHistory"
      :projectID="project.Project_ID"
      :minDate="project.Get_Date"
      :notFromProjectsModule="notFromProjectsModule"
      @managersHistoryUpdated="onManagersHistoryUpdate"
    />
    <ProjectRate
      v-if="project && project.Project_ID && isFinancier && showRateHistory"
      v-model="showRateHistory"
      :projectID="project.Project_ID"
      :minDate="project.Get_Date"
      :notFromProjectsModule="notFromProjectsModule"
      @rateHistoryUpdated="onUpdateRateHistory"
    />
    <ProjectDepartment
      v-if="project && isHRorFinancierOrManager && showDepartmentHistory"
      v-model="showDepartmentHistory"
      :projectID="project.Project_ID"
      :infoForCreatingProjectDepHistory="infoForCreatingProjectDepHistory"
      :minDate="project.Get_Date"
      :notFromProjectsModule="notFromProjectsModule"
      @departmentHistoryUpdated="onUpdateDepartmentHistory"
      @updateProjectCreatingDepInfo="onUpdateProjectCreatingDepInfo"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  components: {
    ProjectDepartment: () => import('./ProjectDepartment.vue'),
    ManagersHistory: () => import('./ManagersHistory.vue'),
    ProjectRate: () => import('./ProjectRate.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Object,
      default: () => {
        return {
          show: false,
          project: null
        }
      }
    },
    notFromProjectsModule: {
      required: false,
      default: false
    }
  },
  name: 'ProjectDetail',
  data () {
    return {
      form: false,
      rules: {
        maxLength: len => v => (v || '').length <= len,
        required: v => !!v || 'This field is required',
        number: v => (/^[-+]?([0-9]+(\.[0-9]*)?|\.[0-9]+)$/.test(v) || '') || 'Only number value'
      },
      editMode: false,
      showAlert: false,
      project: this.value.project ? Object.assign({}, this.value.project) : null,
      searchClient: '',
      showManagersHistory: false,
      showRateHistory: false,
      showDepartmentHistory: false,
      showRemoveDialog: false,
      showIntegrationsDialog: false,
      integrationsActiveTab: -1,
      hasIntegration: false,
      integrationID: null,
      addNewEmployeeToIntegrationMode: false,
      jiraWebhookIntegrationLink: null,
      isEmployeeOnly: null,
      newEmployeeForIntegrationID: null,
      newEmployeeJiraName: '',
      currentRateValue: null,
      searchEmployee: '',
      workersOnCurrentProject: [],
      showWorkersAtProject: false,
      rolesOfCurrentUser: [],
      infoForCreatingProjectDepHistory: [],
      integrations: [
        {
          id: 'jira',
          tabName: 'Jira',
          isDisabled: false,
          fields: [
            {
              label: 'Jira Project Key',
              val: ''
            },
            {
              label: 'Jira Host',
              val: ''
            }
          ]
        },
        {
          id: 'redmine',
          tabName: 'Redmine',
          isDisabled: false,
          fields: [
            {
              label: 'Host',
              val: ''
            },
            {
              label: 'Project identifier',
              val: ''
            },
            {
              label: 'API Key',
              val: ''
            }
          ]
        },
        {
          id: 'gitlab',
          tabName: 'GitLab',
          isDisabled: false,
          fields: [
            {
              label: 'GitLab Host',
              val: ''
            }
          ]
        }
      ],
      integratedEmployeeToJiraProject: [],
      integratedEmployeeHeaders: [
        {
          text: 'Full Name',
          value: 'Full_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Jira Name',
          value: 'Jira_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Actions',
          value: 'Actions',
          width: '50px',
          align: 'center',
          sortable: false,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ],
      workersOnProjectHeaders: [
        {
          text: 'Full Name',
          value: 'Full_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Position',
          value: 'Status',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ]
    }
  },
  async mounted () {
    await this.getRolesOfCurrentUser()
    await this.getEmployeeList()
    this.searchClient = ''
    if (!this.clients.length) {
      this.getClients()
    }
    if (!this.projectStatuses.length) {
      this.getProjectStatuses()
    }
    if (!this.departments.length) {
      this.getDepartments()
    }
    if (this.project && this.project.Project_ID !== null && this.isFinancier) {
      await this.getRateHistory()
    }
  },
  computed: {
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    canSave () {
      let isChanged = false
      if (this.project && this.value.project) {
        for (const key in this.project) {
          if (this.project[key] !== this.value.project[key]) {
            isChanged = true
            break
          }
        }
      }
      return isChanged
    },
    employee () {
      const r = new RegExp(this.searchEmployee, 'gi')
      const currentEmployeeIds = this.integratedEmployeeToJiraProject.map(empl => empl.Employee_ID)
      return this.$store.getters.getEmployee.filter(item => {
        r.lastIndex = 0
        return r.test(item.full_name) && currentEmployeeIds.indexOf(item.employee_id) === -1
      })
    },
    valueForJiraWebhookLink () {
      return this.jiraWebhookIntegrationLink.slice(0, 30) + '...' + this.jiraWebhookIntegrationLink.slice(-17)
    },
    user () {
      return this.$store.getters.getUser
    },
    showWorkers: {
      get () {
        return this.showWorkersAtProject
      },
      set (val) {
        this.showWorkersAtProject = val
      }
    },
    show: {
      get () {
        return this.value.show
      },
      set (val) {
        if (!val) {
          this.editMode = false
        }
        this.$emit('input', {
          show: val,
          project: val ? this.value.project : null
        })
      }
    },
    showIntegrationButton () {
      return this.project && this.project.Project_ID && !this.project.Is_Deleted
    },
    projectWasChanged () {
      let res = false
      if (this.show) {
        for (const key in this.project) {
          if (this.project[key] === this.value.project[key]) continue
          res = true
          break
        }
      }
      return res
    },
    canSaveOrUpdateIntegration () {
      let res = false
      if (this.integrationsActiveTab >= 0 && this.integrations[this.integrationsActiveTab].fields) {
        const allFields = this.integrations[this.integrationsActiveTab].fields.length
        const filledFields = this.integrations[this.integrationsActiveTab].fields.filter(field => field.val).length
        res = allFields === filledFields
      }
      return res
    },
    activeIntegration () {
      if (this.integrationsActiveTab < 0) return null
      return this.integrations[this.integrationsActiveTab] || null
    },
    activeIntegrationId () {
      return this.activeIntegration ? this.activeIntegration.id : null
    },
    activeIntegrationLabel () {
      if (!this.activeIntegration) return 'integration'
      return this.activeIntegration.tabName
    },
    canEdit () {
      return this.isHRorFinancierOrManager && !this.notFromProjectsModule
    },
    isFinancier () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['financier'].indexOf(this.rolesOfCurrentUser[i].RoleName.toLowerCase()) > -1) {
          res = true
          break
        }
      }
      return res
    },
    isHRorFinancierOrManager () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['manager', 'financier', 'hr'].indexOf(this.rolesOfCurrentUser[i].RoleName.toLowerCase()) > -1) {
          res = true
          break
        }
      }
      return res
    },
    canDelete () {
      return this.project && this.canEdit && this.project.Project_ID
    },
    clients () {
      const r = new RegExp(this.searchClient, 'gi')
      return this.$store.getters.getClients.filter(item => {
        r.lastIndex = 0
        return r.test(item.Name) || this.project.Client_ID === item.Client_ID
      })
    },
    projectStatuses () {
      return this.$store.getters.getProjectStatuses.filter(status => status.Status_ID !== 'D')
    },
    statusDescription () {
      return this.projectStatuses.find(item => item.Status_ID === this.project?.Status)?.Description ?? '-'
    },
    workers: {
      get () {
        return this.workersOnCurrentProject
      },
      set (val) {
        this.workersOnCurrentProject = val
      }
    },
    departments () {
      return this.$store.getters.getDepartments
    }
  },
  methods: {
    openManagerHistoryDialog () {
      this.$store.dispatch('setProjectDetailsDialogState', { _state: Object.assign({}, this.project) })
      this.showManagersHistory = true
    },
    onManagersHistoryUpdate () {
      this.$emit('projectUpdated', this.project.Project_ID)
    },
    onUpdateRateHistory (e) {
      if (e && e.history && e.history.length) {
        this.currentRateValue = this.getCurrentRate(e.history)
      } else {
        this.currentRateValue = null
      }
    },
    onUpdateDepartmentHistory () {
      this.$emit('projectUpdated', this.project.Project_ID)
    },
    onUpdateProjectCreatingDepInfo (e) {
      const departmentHistory = this.departments.find(el => el.Dep_Name === e.Dep_Name)
      this.infoForCreatingProjectDepHistory[0] = e

      this.project.Dep_ID = departmentHistory.Dep_ID
      this.project.From_Date = e.From_Date
      this.project.Department = departmentHistory.Dep_Name
    },
    async getRateHistory () {
      await this.axios.get(`/api/project_rate_history/${this.project.Project_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        if (response.data && response.data.length) {
          response.data.forEach(item => {
            item.From_Date = formatLocalDate(item.From_Date)
          })
          this.$store.dispatch('setRateHistoryForProject', {
            projectId: this.project.Project_ID,
            history: response.data
          })
          this.onUpdateRateHistory({ history: response.data })
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getCurrentRate (history) {
      const d = +(new Date(formatLocalDate()))
      const hist = history.filter(h => +(new Date(h.From_Date)) <= d)
        .sort((a, b) => (a.From_Date.toString()).localeCompare(b.From_Date)).reverse()[0]
      return hist ? hist.Rate : null
    },
    onAutocompleteBlur (key, val) {
      if (!this.project[key]) {
        this.project[key] = this.value.project[key]
      }
    },
    async getEmployeeList () {
      await this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getRolesOfCurrentUser () {
      await this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    showIntegrationInf () {
      this.getJiraIntegrationInfo(true)
    },
    openEmployeeCard (e) {
      this.axios.get(`/api/employee/${e.Employee_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('employeeDetailsScreen', {
          show: true,
          employee: res.data[0],
          edit: false
        })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    saveIntegration () {
      if (this.activeIntegrationId === 'jira') {
        const body = {
          DCM_Project_ID: this.project.Project_ID,
          Jira_Project_Key: this.integrations[0].fields[0].val,
          Jira_Host: this.integrations[0].fields[1].val,
          Is_Employee_Only: this.isEmployeeOnly ?? 0
        }
        if (body.DCM_Project_ID && body.Jira_Project_Key && body.Jira_Host) {
          if (this.hasIntegration) {
            body.Integration_ID = this.integrationID
            this.axios.put('/api/integrations/jira', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully updated' })
              this.getJiraIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          } else {
            this.axios.post('/api/integrations/jira', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully saved' })
              this.getJiraIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          }
        }
      }
      if (this.activeIntegrationId === 'redmine') {
        const body = {
          DCM_Project_ID: this.project.Project_ID,
          Redmine_Host: this.integrations[1].fields[0].val,
          Redmine_Project_Identifier: this.integrations[1].fields[1].val,
          Redmine_API_Key: this.integrations[1].fields[2].val
        }
        if (body.DCM_Project_ID && body.Redmine_Project_Identifier && body.Redmine_Host && body.Redmine_API_Key) {
          if (this.hasIntegration) {
            body.Integration_ID = this.integrationID
            this.axios.put('/api/integrations/redmine', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully updated' })
              this.getRedmineIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          } else {
            this.axios.post('/api/integrations/redmine', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully saved' })
              this.getRedmineIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          }
        }
      }
      if (this.activeIntegrationId === 'gitlab') {
        const gitlabTab = this.integrations.find(i => i.id === 'gitlab')
        const body = {
          DCM_Project_ID: this.project.Project_ID,
          Gitlab_Url: gitlabTab.fields[0].val
        }
        if (body.DCM_Project_ID && body.Gitlab_Url) {
          if (this.hasIntegration) {
            body.Integration_ID = this.integrationID
            this.axios.put('/api/integrations/gitlab-project', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully updated' })
              this.getGitlabProjectIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          } else {
            this.axios.post('/api/integrations/gitlab-project', body, {
              headers: {
                Authorization: 'Bearer ' + this.user.token
              }
            }).then(() => {
              this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully saved' })
              this.getGitlabProjectIntegrationInfo()
            }).catch(error => {
              const errorMsg = getApiErrorMessage(error)
              this.$store.dispatch('setError', { errorMsg })
            })
          }
        }
      }
    },
    deleteIntegration () {
      if (this.activeIntegrationId === 'jira') {
        this.axios.delete(`/api/integrations/jira/${this.integrationID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.integrations[0].fields[0].val = null
          this.integrations[0].fields[1].val = null
          this.jiraWebhookIntegrationLink = null
          this.hasIntegration = false
          this.integrationID = null
          this.integrations[0].isDisabled = false
          this.integrations[1].isDisabled = false
          this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully deleted' })
          this.showAlert = false
          this.$emit('updateList')
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
      if (this.activeIntegrationId === 'redmine') {
        this.axios.delete(`/api/integrations/redmine/${this.integrationID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.integrations[1].fields[0].val = null
          this.integrations[1].fields[1].val = null
          this.integrations[1].fields[2].val = null
          this.jiraWebhookIntegrationLink = null
          this.hasIntegration = false
          this.integrationID = null
          this.integrations[0].isDisabled = false
          this.integrations[1].isDisabled = false
          this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully deleted' })
          this.showAlert = false
          this.$emit('updateList')
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
      if (this.activeIntegrationId === 'gitlab') {
        this.axios.delete(`/api/integrations/gitlab-project/${this.integrationID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          const gitlabTab = this.integrations.find(i => i.id === 'gitlab')
          if (gitlabTab) gitlabTab.fields[0].val = null
          this.hasIntegration = false
          this.integrationID = null
          this.$store.dispatch('setSuccess', { successMsg: 'Integration information successfully deleted' })
          this.showAlert = false
          this.$emit('updateList')
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    getJiraIntegrationInfo (showDetailDialog) {
      this.axios.get(`/api/integrations/jira/${this.project.Project_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data[0]) {
          this.integrationID = res.data[0].Integration_ID
          this.integrations[0].fields[0].val = res.data[0].Jira_Project_Key
          this.integrations[0].fields[1].val = res.data[0].Jira_Host
          this.jiraWebhookIntegrationLink = res.data[0].Jira_Webhook_Link
          this.isEmployeeOnly = res.data[0].Is_Employee_Only
          this.integrations[1].isDisabled = true
          this.hasIntegration = true
          this.getAssignedEmployeeToJiraIntegration()
          this.$emit('updateList')
        } else {
          this.integrations[0].fields[0].val = null
          this.integrations[0].fields[1].val = null
          this.jiraWebhookIntegrationLink = null
          this.hasIntegration = false
        }
        if (showDetailDialog) {
          this.integrationsActiveTab = 0
          this.showIntegrationsDialog = true
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getRedmineIntegrationInfo () {
      this.axios.get(`/api/integrations/redmine/${this.project.Project_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data[0]) {
          this.integrationID = res.data[0].Integration_ID
          this.integrations[1].fields[0].val = res.data[0].Redmine_Host
          this.integrations[1].fields[1].val = res.data[0].Redmine_Project_Identifier
          this.integrations[1].fields[2].val = res.data[0].Redmine_API_Key
          this.integrations[0].isDisabled = true
          this.hasIntegration = true
        } else {
          this.integrations[1].fields[0].val = null
          this.integrations[1].fields[1].val = null
          this.integrations[1].fields[2].val = null
          this.hasIntegration = false
        }
        this.jiraWebhookIntegrationLink = null
        this.$emit('updateList')
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getGitlabProjectIntegrationInfo () {
      const gitlabTab = this.integrations.find(i => i.id === 'gitlab')
      this.axios.get(`/api/integrations/gitlab-project/${this.project.Project_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data[0]) {
          this.integrationID = res.data[0].Integration_ID
          if (gitlabTab) gitlabTab.fields[0].val = res.data[0].Gitlab_Url
          this.hasIntegration = true
        } else {
          if (gitlabTab) gitlabTab.fields[0].val = null
          this.hasIntegration = false
          this.integrationID = null
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getAssignedEmployeeToJiraIntegration () {
      this.axios.get(`/api/integrations/get-assigned-employee-to-jira-integration/${this.integrationID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.integratedEmployeeToJiraProject = result.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteEmployeeFromJiraIntegration (item) {
      this.axios.delete(`/api/integrations/unassign-employee-from-jira-integration/${item.Employee_Assigned_To_Integration_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        const i = this.integratedEmployeeToJiraProject.findIndex(int => int.Employee_Assigned_To_Integration_ID === item.Employee_Assigned_To_Integration_ID)
        this.integratedEmployeeToJiraProject.splice(i, 1)
        this.$store.dispatch('setSuccess', { successMsg: 'Employee successfully unassigned' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addNewEmployeeToJiraIntegration () {
      if (this.addNewEmployeeToIntegrationMode) {
        this.axios.post('/api/integrations/assign-employee-to-jira-integration', {
          Integration_ID: this.integrationID,
          Employee_ID: this.newEmployeeForIntegrationID,
          Jira_Name: this.newEmployeeJiraName || null
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(result => {
          this.integratedEmployeeToJiraProject.push(result.data[0])
          this.newEmployeeJiraName = ''
          this.$store.dispatch('setSuccess', { successMsg: 'Employee successfully assigned' })
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
      this.addNewEmployeeToIntegrationMode = !this.addNewEmployeeToIntegrationMode
    },
    saveIsEmployeeOnly (value) {
      this.axios.put('/api/integrations/jira', {
        Integration_ID: this.integrationID,
        DCM_Project_ID: this.project.Project_ID,
        Jira_Project_Key: this.integrations[0].fields[0].val,
        Jira_Host: this.integrations[0].fields[1].val,
        Is_Employee_Only: value
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.$store.dispatch('setSuccess', { successMsg: 'Integration successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateEmployeeJiraName (item, value) {
      this.axios.put('/api/integrations/assign-employee-to-jira-integration', {
        Employee_Assigned_To_Integration_ID: item.Employee_Assigned_To_Integration_ID,
        Jira_Name: value || null
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        item.Jira_Name = value || null
        this.$store.dispatch('setSuccess', { successMsg: 'Jira Name successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    copyJiraWebhookLinkToClipboard () {
      navigator.clipboard.writeText(this.jiraWebhookIntegrationLink).then(() => {
        this.$store.dispatch('setSuccess', { successMsg: 'Link was copied' })
      }, () => {
        this.$store.dispatch('setError', { errorMsg: 'Link wasn\'t copied' })
      })
    },
    onIntegrationTabChange (e) {
      this.hasIntegration = false
      this.integrationID = null
      const tab = this.integrations[e]
      if (!tab) return
      if (tab.id === 'jira') {
        this.getJiraIntegrationInfo()
      }
      if (tab.id === 'redmine') {
        this.getRedmineIntegrationInfo()
      }
      if (tab.id === 'gitlab') {
        this.getGitlabProjectIntegrationInfo()
      }
    },
    getClients () {
      this.axios.get('/api/clients', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setClients', { clients: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getProjectStatuses () {
      this.axios.get('/api/project_statuses', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setProjectStatuses', { projectStatuses: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getDepartments () {
      this.axios.get('/api/department', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setDepartments', { departments: res.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    onSaveClick () {
      this.$refs.projectForm.validate()
      if (this.form) {
        this.$emit('saveProject', this.project)
      }
    },
    onDeleteClick () {
      if (this.project.Is_Deleted) {
        this.project.Is_Deleted = false
        this.project.Deleted_Notes = null
      } else {
        const date = new Date().toLocaleString()
        this.project.Is_Deleted = true
        this.project.Deleted_Notes = `Deleted by: ${this.user.login}, Date: ${date}`
      }
      this.$emit('saveProject', this.project)
    },
    onCancelClick () {
      this.project = Object.assign({}, this.value.project)
      this.editMode = false
      this.$store.dispatch('setProjectDetailsDialogState', { state: null })
      if (!this.project.Project_ID) {
        this.$emit('closeDetails')
      }
      this.show = false
    },
    showAllWorkersOnCurrentProject () {
      this.axios.get(`/api/employee_at_project/${this.project.Project_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.workers = result.data
        this.showWorkers = true
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    goToReportsNewPage () {
      this.$router.push({
        name: 'ManagerReports',
        params: {
          Project_ID: this.project.Project_ID
        }
      })
    },
    clearDepartment () {
      this.project.Dep_ID = null
      this.project.Department = ''
    }
  },
  watch: {
    value (newVal) {
      this.project = newVal.project ? Object.assign({}, newVal.project) : null
    }
  }
}
</script>
<style lang="scss" scoped>
  ::v-deep .v-card {
    display: flex;
    flex-direction: column;
  }
  ::v-deep .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
  .my-7 {
    margin-top: 30px !important;
    margin-bottom: 30px !important;
  }
  .no-project-id-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;

    position: absolute;
    z-index: 2;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 0;
    padding: 30px;
    font-size: 1.5rem;
    line-height: 1.2;

    background-color: transparent;

    color: rgba(0, 0, 0, 0.7);
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.25);

    & + .form-fields-container {
      filter: blur(5px);
    }
  }

  ::v-deep .theme--dark {
    .no-project-id-placeholder {
      color: rgba(255, 255, 255, 0.85);
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.15);
    }
  }
</style>
